import { useMutation, useQuery, useQueryClient } from "react-query";
import { authHeaders } from "./auth-header";
import { getApiErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import {
  CreatePersonFingerprintModel,
  DeletePersonFingerprintModel,
  GetPersonFingerprintModel,
  MutateResponsePersonFingerprintModel,
  QueryKeyPersonFingerprint,
  UpdatePersonFingerprintModel,
  createPersonFingerprintController,
  deletePersonFingerprintController,
  getPersonFingerprintController,
  updatePersonFingerprintController,
} from "@core/model/query-model-person-fingerprint";
import { useSessionContext } from "./session.context";

export function useQyGetPersonFingerprintByUserCode(
  userCode: string,
  enabled = true,
  onSuccess?:
    | ((data: GetPersonFingerprintModel[] | undefined) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (userCode: string) => {
    await checkSession();

    showProgress();
    const operation = await getPersonFingerprintController(
      userCode,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetPersonFingerprintModel[] | undefined;
  };

  return useQuery({
    enabled: enabled && !!userCode,
    queryKey: [QueryKeyPersonFingerprint, userCode],
    queryFn: () => apiFn(userCode),
    retry: false,
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      if (onError) {
        onError(err);
        return;
      }
      const message = getApiErrorMessage(err);
      showError(message);
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCreatePersonFingerprint(
  onSuccess?:
    | ((data: MutateResponsePersonFingerprintModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: CreatePersonFingerprintModel) => {
    await checkSession();

    showProgress();
    const operation = await createPersonFingerprintController(
      payload,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as MutateResponsePersonFingerprintModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyPersonFingerprint);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyUpdatePersonFingerprint(
  onSuccess?:
    | ((data: MutateResponsePersonFingerprintModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: UpdatePersonFingerprintModel) => {
    await checkSession();

    showProgress();
    const operation = await updatePersonFingerprintController(
      payload,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as MutateResponsePersonFingerprintModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyPersonFingerprint);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyDeletePersonFingerprint(
  onSuccess?:
    | ((data: MutateResponsePersonFingerprintModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DeletePersonFingerprintModel) => {
    await checkSession();

    showProgress();
    const operation = await deletePersonFingerprintController(
      payload,
      authHeaders()
    );
    const response = (await operation()).data;
    return response as MutateResponsePersonFingerprintModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyPersonFingerprint);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
